<template>
  <div
    class="code-select-item"
    :class="{ highlighted: isHighlighted }"
    data-test-id="code-select-item"
    :data-test-code-selected="isSelected ? 'true' : 'false'"
    :data-test-code-disabled="isDisabled ? 'true' : 'false'"
  >
    <IconOnHover :is-disabled="isDisabled" @click="secondaryAction">
      <slot name="secondary-action" />
    </IconOnHover>

    <CheckBoxToggle
      v-if="controlStyle === 'toggle'"
      class="code-select-item__checkbox"
      :id="id"
      ref="Input"
      :checked="isSelected"
      :disabled="isDisabled"
      :position="hasSlotSupplement ? 'loose' : 'end'"
      @update:checked="$emit('toggle', $event)"
      @keyup.enter.exact="$emit('toggle', $event)"
      @focusout="stopFocusBubble"
    >
      <div class="primary-text" data-test-id="code-select-item__text">
        <span :title="title">{{ primaryText }}</span>
        <span class="secondary-text" :title="title" v-if="secondaryText"> &nbsp;{{ secondaryText }} </span>
      </div>

      <div
        class="supplement"
        v-if="hasSlotSupplement"
        :title="supplementTitle"
        data-test-id="code-select-item__supplement"
      >
        <slot name="supplement" />
      </div>
    </CheckBoxToggle>
    <RadioButton
      v-else
      :modelValue="true"
      :model="isSelected"
      class="code-select-item__radio-button"
      :disabled="isDisabled"
      @click="$emit('toggle', $event)"
      @keyup.enter.exact="$emit('toggle', $event)"
      @focusout="stopFocusBubble"
    >
      <div class="primary-text" data-test-id="code-select-item__text">
        <span :title="title">{{ primaryText }}</span>
        <span class="secondary-text" :title="title" v-if="secondaryText"> &nbsp;{{ secondaryText }} </span>
      </div>

      <div
        class="supplement"
        v-if="hasSlotSupplement"
        :title="supplementTitle"
        data-test-id="code-select-item__supplement"
      >
        <slot name="supplement" />
      </div>
    </RadioButton>
  </div>
</template>
<script lang="ts">
import { defineComponent, PropType } from 'vue';
import IconOnHover from '@components/IconOnHover.vue';
import { pseudoRandomId } from '@utils/pseudoRandomId';
import { hasSlotContent } from '@components/slots';

export default defineComponent({
  name: 'CodeSelectItem',
  emits: { toggle: null },

  components: {
    IconOnHover,
  },

  props: {
    primaryText: {
      type: String,
      required: true,
    },
    secondaryText: {
      type: String,
      required: false,
      default: null,
    },
    supplementTitle: {
      type: String,
      required: false,
      default: null,
    },
    /**
     * The item is highlighted, akin to hover, but with keyboard
     */
    isHighlighted: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * The item should be focussed
     */
    isFocussed: {
      type: Boolean,
      required: false,
      default: false,
    },

    isSelected: {
      type: Boolean,
      required: true,
    },

    isDisabled: {
      type: Boolean,
      required: false,
      default: false,
    },

    controlStyle: {
      type: String as PropType<'toggle' | 'radio'>,
      default: 'toggle',
    },
  },

  data() {
    return {
      id: pseudoRandomId(),
    };
  },

  computed: {
    title() {
      if (!this.secondaryText) return this.primaryText;

      return `${this.primaryText} (${this.secondaryText})`;
    },

    hasSlotSupplement() {
      return hasSlotContent(this.$slots.supplement, {});
    },
  },

  methods: {
    secondaryAction($event: MouseEvent) {
      $event.stopPropagation();
    },
    stopFocusBubble(event: FocusEvent) {
      event.stopPropagation();
    },
  },

  watch: {
    isFocussed(newValue) {
      if (newValue) {
        this.$refs.Input?.focus();
      }
    },
  },
});
</script>

<style lang="scss" scoped>
@use 'settings/colors';

.code-select-item {
  height: 32px;
  display: flex;
  gap: 8px;
  align-items: center;
  border-radius: 8px;
  padding: 0 4px;

  &:hover,
  &.highlighted {
    background-color: colors.$color-grey-100;
  }
}

.code-select-item__checkbox {
  min-height: 32px;
}

.code-select-item__radio-button {
  min-width: 0;
  width: 100%;
  height: 100%;
}

.code-select-item__button {
  display: flex;
  gap: 8px;
  align-items: center;
  overflow: hidden;
  height: 100%;
  width: 100%;

  &:focus {
    outline: none;
  }
}

.primary-text {
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  line-height: 18px;
}

.secondary-text {
  color: colors.$color-grey-500;
}

.supplement {
  margin-left: auto;
  font-size: 11px;
  color: colors.$color-grey-500;
  font-weight: 400;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
}
</style>
